import { type AnyElement, type AnyElementData } from '../all-element-types.ts';
import {
  type ElementData,
  type Element,
  elementType,
  type ElementType,
} from '../element.ts';

import { type ConnectorElementData } from './connector.ts';

/**
 * Represents the 'type' of a smart connector.
 */
export enum RelationKind {
  /**
   * @description A ISSUE_HIERARCHY type infers that the connector is between
   * a parent and child issue. The {@link RelationDirection} of the connector will accordingly
   * point towards the child.
   * @example A Jira epic & Jira issue.
   */
  ISSUE_HIERARCHY = 1,
  /**
   * @description A link type infers the connector is between two issues
   * which exist at the same level. I.e., there is no hierarchy between them.
   * @example a Jira issue & Jira issue
   */
  ISSUE_LINK = 2,
}

/**
 * Represents the direction a smart connector goes in.
 * @description Consider the case of a Jira issue which blocks another Jira
 * issue. This can be represented using either 'blocks' or 'blocked by'.
 */
export enum RelationDirection {
  /**
   * @description Direction is in direction of {@link SmartConnectorElement.sourceElement}. I.e, ends at source.
   */
  TOWARDS_SOURCE = 1,
  /**
   * @description Direction is in direction of {@link SmartConnectorElement.targetElement}. I.e, ends at target.
   */
  TOWARDS_TARGET = 2,
}

/**
 * Which description of a Jira issue link type reads from the source issue to the
 * destination issue: `outward` for "source blocks destination", `inward` for "source is
 * blocked by destination".
 */
export type IssueLinkDirection = 'inward' | 'outward';

type IssueLinkEndpoints = {
  readonly inwardIssue?: { readonly key: string };
  readonly outwardIssue?: { readonly key: string };
};

const normalizeIssueKey = (key: string) => key.trim().toUpperCase();

/**
 * Jira stores "A blocks B" with A as the inward issue and B as the outward issue. An
 * issue's own `issuelinks` omit that issue, so a link may name only its other end.
 */
export function getIssueLinkDirection(
  link: IssueLinkEndpoints,
  sourceIssueKey: string,
  destIssueKey: string,
): IssueLinkDirection | undefined {
  const inward = link.inwardIssue && normalizeIssueKey(link.inwardIssue.key);
  const outward = link.outwardIssue && normalizeIssueKey(link.outwardIssue.key);
  const source = normalizeIssueKey(sourceIssueKey);
  const dest = normalizeIssueKey(destIssueKey);

  const readsOutward = (inward ?? source) === source && (outward ?? dest) === dest;
  const readsInward = (inward ?? dest) === dest && (outward ?? source) === source;
  if (readsOutward === readsInward) {
    return undefined;
  }
  return readsOutward ? 'outward' : 'inward';
}

/**
 * For a smart connector drawn from the source issue card to the destination card, the
 * arrow follows the link's outward description.
 */
export function getRelationDirectionForIssueLink(
  direction: IssueLinkDirection,
): RelationDirection {
  return direction === 'outward'
    ? RelationDirection.TOWARDS_TARGET
    : RelationDirection.TOWARDS_SOURCE;
}

export interface SmartConnectorElementData
  extends Omit<ConnectorElementData, 'type'>, ElementData<ElementType> {
  /**
   * The kind of relation this smart connector represents.
   */
  kind: RelationKind;
  /**
   * The direction this relation goes in.
   * @example From parent -> child
   * @example From ISSUE_LINK -> ISSUE_LINK or ISSUE_LINK <- ISSUE_LINK.
   */
  direction: RelationDirection;
  /**
   * The external id that this relationship refers to
   * @example Jira issue link ID
   */
  relationshipId: string;
  /**
   * The proportion of the connector's line that the label should be displayed at.
   */
  proportion: number;
}

export interface SmartConnectorElementPasteData extends SmartConnectorElementData {
  /**
   * The index of the connector's source element in the array of copied elements.
   */
  sourceIndex: number;
  /**
   * The index of the connector's target element in the array of copied elements.
   */
  targetIndex: number;
}

export interface SmartConnectorElement
  extends SmartConnectorElementData, Element<ElementType> {}

export function isSmartConnector(
  element: AnyElement | undefined,
): element is SmartConnectorElement {
  return element?.type === smartConnectorElementType;
}

export function isSmartConnectorData(
  element: AnyElementData,
): element is SmartConnectorElementData {
  return element?.type === smartConnectorElementType;
}

export const smartConnectorElementType = elementType('smartConnector');
